<template>
  <CSidebar
    fixed
    class="preika-sidebar"
    :minimize="minimize"
    :show="show"
    @update:show="value => $store.commit('set', ['sidebarShow', value])"
  >
    <CSidebarBrand>
      <div
        class="preika-sidebar-brand"
        role="button"
        tabindex="0"
        aria-label="ダッシュボードへ移動"
        @click="redirectToPage"
        @keyup.enter="redirectToPage"
      >
        <img src="/img/avatars/preika.png" :alt="productName" class="preika-sidebar-brand__logo">
        <span class="preika-sidebar-brand__copy" aria-hidden="true">
          <span class="preika-sidebar-brand__name">{{ brand }}</span>
          <span class="preika-sidebar-brand__area">{{ productArea }}</span>
        </span>
        <!-- 畳んだときの印。語を割らずに、別の1文字を出す。 -->
        <span class="preika-sidebar-brand__mark" aria-hidden="true">P</span>
      </div>
    </CSidebarBrand>

    <CRenderFunction flat :content-to-render="navItems"/>

    <div class="preika-sidebar-meta">
      <span class="preika-sidebar-meta__dot"></span>
      <span class="preika-sidebar-meta__copy">{{ workspaceLabel }}</span>
    </div>
  </CSidebar>
</template>

<script>
import axios from 'axios'
import nav from './_nav_admin'
import { BRAND, PRODUCT_AREA, PRODUCT_NAME } from '@/common/productName'
import { buildSidebarNavigation } from '../utils/sidebarNavigation'

export default {
  name: 'TheAdminSidebar',
  nav,

  props: {
    accessContext: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      navItems: []
    }
  },

  computed: {
    /** 売るときの正式な表記。**1か所から取る。** */
    /** 銘の「勤怠」の部分。**シリーズの中のどれかを示す。** */
    /** シリーズの名前。 */
    brand () {
      return BRAND
    },

    productArea () {
      return PRODUCT_AREA
    },

    productName () {
      return PRODUCT_NAME
    },

    show () { return this.$store.state.sidebarShow },
    minimize () { return this.$store.state.sidebarMinimize },
    sidebarBadgeRefreshTick () { return this.$store.state.sidebarBadgeRefreshTick },
    workspaceLabel () {
      const roles = this.accessContext.roles || []
      /*
       * **運営側だけ、シリーズの名前で出す。**買った会社の人は入れない
       * 画面へ入っているときの表示なので、ここに勤怠と書くとかえって
       * 分からなくなる（給与や評価の運営もここへ来る）。
       */
      if (roles.includes('PLATFORM_SUPER_ADMIN')) return 'PREIKA 運営（緊急）'
      if (roles.includes('PLATFORM_OPERATIONS_ADMIN')) return 'PREIKA 運営'
      if (roles.includes('PLATFORM_SUPPORT_VIEWER')) return 'PREIKA サポート'
      /*
       * ここから下は買った会社の人が見る。**製品の名前と役割**にする。
       *
       * 英語をやめたのは、Clerk が英語では「受付係」に読めるため。
       * 日本語の「一般」とはずれていた（2026-08-27 利用者の指摘）。
       */
      if (roles.includes('TENANT_OWNER')) return this.productName + '　オーナー'
      if (roles.includes('TENANT_SYSTEM_ADMIN')) return this.productName + '　システム管理'
      if (roles.includes('EXECUTIVE_ADMIN')) return this.productName + '　管理'
      if (roles.includes('CLERK')) return this.productName + '　担当'
      return this.productName
    }
  },

  watch: {
    accessContext: {
      immediate: true,
      deep: true,
      handler () {
        this.rebuildNavigation()
      }
    },
    sidebarBadgeRefreshTick () { this.fetchSidebarBadges() }
  },

  mounted () {
    this.fetchSidebarBadges()
  },

  methods: {
    redirectToPage () {
      if (this.$route.path !== '/dashboard') this.$router.push('/dashboard')
    },

    rebuildNavigation () {
      // 組み立ては utils/sidebarNavigation.js へ切り出した（権限管理の画面でも同じ判定で使うため）。
      this.navItems = buildSidebarNavigation(this.accessContext)
      this.$nextTick(this.fetchSidebarBadges)
    },

    fetchSidebarBadges () {
      if (this.findNavItemByTo(this.navItems, '/submission_approval/report_list')) {
        this.fetchSubmissionListCount()
      }
      if (this.findNavItemByTo(this.navItems, '/submission_approval/submission_management')) {
        this.fetchApprovalPendingCount()
      }
    },

    fetchSubmissionListCount () {
      const origin = process.env.VUE_APP_API_ORIGIN
      return axios.get(origin + '/submission/list')
        .then(response => {
          const count = Array.isArray(response.data) ? response.data.length : 0
          this.setNavBadgeByTo('/submission_approval/report_list', count)
        })
        .catch(error => {
          axios.post(`${origin}/error/frontError`, { error: String(error) }, { headers: { 'Content-Type': 'application/json' } }).then(() => {}).catch(() => {})
          this.setNavBadgeByTo('/submission_approval/report_list', 0)
        })
    },

    setNavBadgeByTo (to, count) {
      const item = this.findNavItemByTo(this.navItems, to)
      if (!item) return
      const numericCount = Number(count)
      if (!Number.isFinite(numericCount) || numericCount <= 0) {
        this.$set(item, 'badge', null)
        return
      }
      this.$set(item, 'badge', {
        color: 'danger',
        text: numericCount > 99 ? '99+' : String(numericCount)
      })
    },

    findNavItemByTo (items, to) {
      if (!Array.isArray(items)) return null
      for (let index = 0; index < items.length; index += 1) {
        const item = items[index]
        if (item && item.to === to) return item
        if (item && Array.isArray(item._children)) {
          const found = this.findNavItemByTo(item._children, to)
          if (found) return found
        }
      }
      return null
    },

    fetchApprovalPendingCount () {
      const origin = process.env.VUE_APP_API_ORIGIN
      return axios.get(origin + '/document/approval/list')
        .then(response => {
          const count = Array.isArray(response.data) ? response.data.length : 0
          this.setNavBadgeByTo('/submission_approval/submission_management', count)
        })
        .catch(error => {
          axios.post(`${origin}/error/frontError`, { error: String(error) }, { headers: { 'Content-Type': 'application/json' } }).then(() => {}).catch(() => {})
          this.setNavBadgeByTo('/submission_approval/submission_management', 0)
        })
    }
  }
}
</script>
